// components/portal/SourcingCenterView.tsx
import { ResearchRowStyles } from '@/components/portal/ResearchRowStyles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { PageEmoji } from '@/components/ui/PageEmoji';
import UserAvatar from '@/components/UserAvatar';
import { ExternalLink, Map as MapIcon } from 'lucide-react';
import { loadPlans, openMerchPlanWindow } from '@/components/footfall/pulseState';
import { canAccessFootfall } from '#lib/access/footfallAccess';
import { SourceEmojiPicker } from '@/components/portal/SourceEmojiPicker';
import {
  resolveRunNarrationLine,
  resolveRunTruncationLine,
  type SourcingTruncationReason,
} from '#lib/leasing/run-narration';
import { CampaignDashboard } from './CampaignDashboard';
import { SourceAssetsDashboard, SourceDashboardToggle } from './SourceAssetsDashboard';
import { PortfolioFitsTable } from '@/components/entities/tables/portfolioFits/PortfolioFitsTable';
import { EnrichProgressBar } from '@/components/portal/EnrichProgressBar';
import { LeadsTable } from '@/components/entities/tables/leads/LeadsTable';
import { useCampaignTableReviewMode, type TableColumnDef } from '@/components/portal/Table';
import { PortfolioFitsGrid } from '@/components/entities/grids/PortfolioFitsGrid';
import { LeadsGrid } from '@/components/entities/grids/LeadsGrid';
// The one reader of the re-run marker, shared with the worker and the campaigns list route: the
// marker has had two shapes (one brand, then a list) and only this reader knows both.
import { readBrandRerunMarker, ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';
import { normalizeSourcingSelection } from '#lib/leasing/sourcingSelection';
import {
  countLeadsInFlight,
  isLeadInFlight,
  isCampaignSelectionStillValid,
  isLeadRevealedWhileBuilding,
  resolveOpenCampaignScope,
  shouldRecallDealerOnLanding,
} from '#lib/leasing/source-view-guards';
import { SOURCE_EMPTY_PORTFOLIO_ID } from '#lib/source-defaults';
import type { InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { readCampaignRow, resolveCampaignPropertyId } from '#lib/leasing/campaignRow';
import { activeCampaignResearch, campaignSelection } from '#lib/leasing/researchPass';
import { BrandMark } from '@/components/ui/BrandMark';
import type {
  LeasingLeadDTO,
  LeasingLeadFilters,
  LeasingPortfolioDTO,
  LeasingPropertyDTO,
  LeasingUnitDTO,
  PortfolioFitDTO,
  PortfolioFitFilters,
  SourcingTargetRuntimeState,
} from '#types/types';

interface SourcingCenterViewProps {
  entityType: 'Portfolio' | 'Property' | 'Unit';
  entityId: string;
}

// Historical import site of the empty-dashboard sentinel, kept for its existing callers. It now
// lives in #lib/source-defaults, beside the other Source landing defaults, because the Kernel
// needs it too and importing this component from kernel/ drags the whole kernel graph back in.
export { SOURCE_EMPTY_PORTFOLIO_ID };

// Sentinel "campaign id" used while a new campaign is being drafted (before it
// is launched). It matches no real campaign, so the fits table renders empty;
// on launch the view re-points to the real campaign id and populates.
const SOURCE_DRAFT_CAMPAIGN_ID = '__source_draft_campaign__';

const buildUnitLabel = (unit: LeasingUnitDTO | undefined, unitId: string, lang: string): string => {
  if (!unit) return `${lang === 'fr' ? 'Unité' : 'Unit'} ${unitId.slice(0, 8)}`;
  const identifier = (unit.unitIdentifier || '').trim() || unitId.slice(0, 8);
  const area = typeof unit.areaSqm === 'number' && Number.isFinite(unit.areaSqm)
    ? `${Math.round(unit.areaSqm)} m²`
    : null;
  return area ? `${identifier} · ${area}` : identifier;
};

// Static props handed to the memoized tables below. A literal written inline in the JSX is a new
// object on every render, and this view re-renders on every socket snapshot of a running campaign:
// that alone defeated React.memo on LeadsTable / PortfolioFitsTable and re-rendered the whole
// shortlist each time.
const LEAD_SCORE_SORT: Partial<LeasingLeadFilters> = { sortBy: 'fitScore', sortDir: 'desc' };
const FIT_SCORE_SORT: Partial<PortfolioFitFilters> = { sortBy: 'score', sortDir: 'desc' };
// Review mode drives the row set through scopedIds, so it starts from no filters at all.
const REVIEW_MODE_INITIAL_FILTERS = {};
const formatSourceDate = (value: string | null | undefined, lang: string): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const SourcingCenterView = ({ entityType, entityId }: SourcingCenterViewProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const sourceDashboardTab = useKernelStore((state) => state.sourceDashboardTab);
  // What the OPEN campaign covered. Derived, never a mode: there is one sourcing operation, and
  // which table / panel / lock applies is a property of the run on screen, not of a toggle.
  const scope = useKernelStore(resolveOpenCampaignScope);
  const portfolioRegistry = useKernelStore((state) => state.inventory.Portfolio.registry);
  const propertyRegistry = useKernelStore((state) => state.inventory.Property.registry);
  const unitRegistry = useKernelStore((state) => state.inventory.Unit.registry);
  // THE UNIFIED registry, which is the one that gets hydrated.
  //
  // This read said `PortfolioFitCampaign` while the hydration below calls
  // ensureInventoryIds('Campaign', ...) - written to one slice, read from another, so every
  // lookup came back undefined. That single mismatch was two visible bugs at once: the page
  // header showed "All assets" for a run the user had renamed (the name is on the campaign it
  // could not find) and the shortlist owner never appeared (same object, same miss).
  //
  // The unified row is also the RIGHT one to read: it carries config.name, createdByUserId and
  // targetKind for every granularity, where PortfolioFitCampaign only ever held estate runs.
  const campaignRegistry = useKernelStore((state) => state.inventory.Campaign.registry);
  // ONE READER FOR THE WHOLE FILE. Five places below used to open a registry each and pick their
  // own order between the two names for the same row - and two of them picked differently. The
  // shared reader takes the unified row first and falls back to the legacy name only where it is
  // the only one hydrated (a launch response, a cold entity page).
  const readCampaign = useKernelStore(
    (state) => (campaignId: string | null | undefined) => readCampaignRow(state.inventory, campaignId),
  );

  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');
  const [viewMode, setViewMode] = useState<InventoryVisualMode>('table');
  // Campaign opened from the dashboard (null = dashboard). Lifted to the kernel
  // store so the right-bar setup form can open the new campaign right after it
  // launches, and so "New campaign" can show an empty draft table here.
  const sourceSelectedCampaignId = useKernelStore((state) => state.sourceSelectedCampaignId);
  const sourceCreatingCampaign = useKernelStore((state) => state.sourceCreatingCampaign);
  const sourcePreparingCampaign = useKernelStore((state) => state.sourcePreparingCampaign);
  const sourceEnrichInFlight = useKernelStore((state) => state.sourceEnrichInFlight);
  // >0 while a rubric re-apply / doctrine re-judge the user just fired is rewriting this
  // shortlist's scores. Lit at the kernel level, so it does not matter which surface (right-bar
  // panel, campaign renderer, agent command) actually triggered the re-score.
  const isRescoringShortlist = useKernelStore((state) => (state.sourceRescoreInFlight ?? 0) > 0);
  const sourceSelectedFitIds = useKernelStore((state) => state.sourceSelectedFitIds);
  const selectedCampaignId = sourceSelectedCampaignId;
  const setSelectedCampaignId = useCallback(
    (id: string | null) => {
      // A campaign never opens without its own conversation, and never closes while keeping it:
      // both halves of that transition are kernel methods, and this view only says which one.
      const k = kernel.current;
      if (!k) return;
      if (id) void k.openSourceCampaign(id);
      else k.closeSourceCampaign();
    },
    [kernel]
  );

  // The expanded brand row publishes its id to the kernel store so the right
  // bar (mounted separately) can swap to the brand context panel.
  const setFocusedFit = useCallback((id: string | null) => {
    kernel.current?.setState({ sourceFocusedFitId: id });
  }, [kernel]);

  // ...and reads it back, so the store is the ONLY holder of "which row is open". The composer's
  // subject chip clears it too; without this the table kept its own copy and the row stayed
  // expanded after the chip deselected, taking two clicks to re-open.
  const focusedFitId = useKernelStore((state) => state.sourceFocusedFitId);

  // Clear the bulk selection when the open campaign changes (or closes), so a stale
  // selection never drives the right-bar bulk-enrich panel on the dashboard / another campaign.
  useEffect(() => {
    kernel.current?.setState({ sourceSelectedFitIds: [] });
  }, [sourceSelectedCampaignId, kernel]);

  useEffect(() => {
    const k = kernel.current;
    if (!k || !entityId || entityId === SOURCE_EMPTY_PORTFOLIO_ID) return;

    void (async () => {
      if (entityType === 'Portfolio') {
        await k.syncEntity('Portfolio', entityId);
        return;
      }

      if (entityType === 'Property') {
        const property = (await k.syncEntity('Property', entityId)) as LeasingPropertyDTO | null;
        if (property?.portfolioId) {
          await k.syncEntity('Portfolio', property.portfolioId);
        }
        return;
      }

      const unit = (await k.syncEntity('Unit', entityId)) as LeasingUnitDTO | null;
      if (!unit?.propertyId) return;
      const property = (await k.syncEntity('Property', unit.propertyId)) as LeasingPropertyDTO | null;
      if (property?.portfolioId) {
        await k.syncEntity('Portfolio', property.portfolioId);
      }
    })();
  }, [entityId, entityType, kernel]);

  const focusedUnit = entityType === 'Unit' ? (unitRegistry[entityId] as LeasingUnitDTO | undefined) : undefined;

  const focusedPropertyId = useMemo(() => {
    if (entityType === 'Property') return entityId;
    if (entityType === 'Unit') return focusedUnit?.propertyId ?? '';
    return '';
  }, [entityType, entityId, focusedUnit?.propertyId]);

  const focusedProperty = focusedPropertyId ? (propertyRegistry[focusedPropertyId] as LeasingPropertyDTO | undefined) : undefined;

  const isEmptySource = entityType === 'Portfolio' && entityId === SOURCE_EMPTY_PORTFOLIO_ID;

  const resolvedPortfolioId = useMemo(() => {
    if (entityType === 'Portfolio') return entityId === SOURCE_EMPTY_PORTFOLIO_ID ? '' : entityId;
    return focusedProperty?.portfolioId ?? '';
  }, [entityType, entityId, focusedProperty?.portfolioId]);

  // Has the selected campaign's row reached ANY campaign registry yet? Only the absent->present
  // transition matters (it re-triggers the validity check below once, when there is finally an
  // ownership to check); the row's contents are still read fresh inside the effect.
  const selectedCampaignRowLoaded = Boolean(
    sourceSelectedCampaignId &&
      readCampaign(sourceSelectedCampaignId),
  );

  // NOTHING WAS EVER FETCHING IT, and everything downstream reads its absence as an answer.
  //
  // `resolveOpenCampaignScope` derives portfolio-vs-asset from the campaign's own `targetKind`,
  // and when the row is in no registry it falls back to `asset` - deliberately, because that is
  // the shape the agent locks were written against. But a row that nobody loads is absent
  // forever, so a portfolio run restored from its conversation was permanently mistaken for an
  // asset run: the right bar asked /sourcing-campaigns/<id>, got "Sourcing campaign not found"
  // because the id is a PortfolioFitCampaign, and the leads table showed "No results found"
  // because a portfolio run produces PortfolioFit rows, not Leads. A completed campaign looked
  // like it had produced nothing.
  //
  // The fallback is not the bug; the missing fetch is. One hydration and the resolver gets the
  // real answer.
  useEffect(() => {
    const k = kernel.current;
    if (!k || !sourceSelectedCampaignId || selectedCampaignRowLoaded) return;
    void k.ensureInventoryIds('Campaign', [sourceSelectedCampaignId]).catch(() => undefined);
  }, [sourceSelectedCampaignId, selectedCampaignRowLoaded, kernel]);

  // Reset the right-bar brand focus + open/draft campaign when the focused entity or the
  // portfolio changes, or the editor unmounts, so navigating away always drops back to the
  // central dashboard instead of leaving a now-mismatched campaign open (which rendered
  // "nothing to display"). `scope` is derived from the open campaign and MUST stay in the deps:
  // it changes exactly when the open run does.
  useEffect(() => {
    setFocusedFit(null);
    // Drop the open campaign only when it no longer belongs to the entity now in focus. A
    // campaign just launched must stay selected so the central view and the working aura follow
    // the live run instead of snapping back to the dashboard. Registries are read intentionally
    // without being deps: this effect must react to entity navigation, not to every registry
    // refresh during a running campaign.
    // The unified row FIRST, and the asset family only as a fallback for a state where just that
    // one has been hydrated. The portfolio family was the third option here and it holds no rows.
    const selectedCampaign = sourceSelectedCampaignId
      ? (readCampaign(sourceSelectedCampaignId) as { propertyId?: string; portfolioId?: string } | undefined)
      : undefined;
    // Keep the open campaign only when it both belongs to the current scope's
    // registry AND to the entity now in focus. Without the ownership check a
    // campaign opened for property P1 would stay selected after navigating to P2,
    // leaving the leads table filtered on { propertyId: P2, lastCampaignId: <P1's
    // campaign> }, an impossible intersection that renders empty. Ownership fields
    // that are absent don't force a clear (avoids wiping a just-launched campaign
    // before its DTO fully hydrates).
    // The decision lives in lib/leasing/source-view-guards (tested there). An ABSENT row is
    // "not hydrated yet", not "wrong": at boot, a conversation bound to a campaign restores the
    // selection before any registry has loaded, and treating the missing row as invalid wiped
    // that selection every time - the user landed on the dashboard with the campaign's chat
    // talking next to it.
    const selectionValid = isCampaignSelectionStillValid({
      selectedCampaign,
      scope,
      entityType,
      entityId,
      resolvedPortfolioId,
    });
    // Only a SELECTED campaign can be a mismatched selection. A draft has no campaign row by
    // construction, so `selectedCampaign` is always undefined for one and this guard used to read
    // that as "invalid" and close the setup form on any re-focus of the subject. That is now a
    // routine event: creating a campaign opens with an agent turn (campaign onboarding), and a
    // dealer turn stamps its target subject, which silently threw the user out of the form
    // they were still filling in, chat panel included.
    if (sourceSelectedCampaignId && !selectionValid) {
      kernel.current?.setState({ sourceSelectedCampaignId: null, sourceCreatingCampaign: false });
    }
    return () => setFocusedFit(null);
    // `selectedCampaignRowLoaded` closes the hydration window: a selection restored at boot
    // passes validation while its row is absent, and without this dep the ownership check would
    // never re-run when the row lands - a contradicting row would keep an impossible selection
    // until the next scope/entity change. The boolean flips exactly once (absent -> present), so
    // this does NOT reintroduce a re-run on every registry refresh.
  }, [setFocusedFit, kernel, resolvedPortfolioId, entityType, entityId, scope, selectedCampaignRowLoaded]);

  // NOTE: the "portfolio scope goes straight to the reference shortlist" auto-resolve lived here.
  // It was reachable only from the Portfolio/Asset/Unit mode toggle, which is gone: a whole-
  // portfolio run is now just a campaign card on the one dashboard, and opening it shows the
  // accumulated reference list exactly as before.

  const portfolio = resolvedPortfolioId
    ? (portfolioRegistry[resolvedPortfolioId] as LeasingPortfolioDTO | undefined)
    : undefined;

  const portfolioSourcingSelection = useMemo(
    () => normalizeSourcingSelection((portfolio as (LeasingPortfolioDTO & { sourcingSelection?: SourcingTargetRuntimeState }) | undefined)?.sourcingSelection),
    [portfolio]
  );
  const focusedPropertySourcingSelection = useMemo(
    () => normalizeSourcingSelection((focusedProperty as (LeasingPropertyDTO & { sourcingSelection?: SourcingTargetRuntimeState }) | undefined)?.sourcingSelection),
    [focusedProperty]
  );
  const focusedUnitSourcingSelection = useMemo(
    () => normalizeSourcingSelection((focusedUnit as (LeasingUnitDTO & { sourcingSelection?: SourcingTargetRuntimeState }) | undefined)?.sourcingSelection),
    [focusedUnit]
  );

  const propertyIds = useMemo(() => {
    const raw = Array.isArray(portfolio?.propertyIds) ? portfolio.propertyIds : [];
    const unique = Array.from(new Set(raw.filter((id): id is string => typeof id === 'string' && id.length > 0)));
    if (unique.length > 0) return unique;
    if (focusedPropertyId) return [focusedPropertyId];
    return [];
  }, [portfolio?.propertyIds, focusedPropertyId]);

  useEffect(() => {
    const k = kernel.current;
    if (!k || propertyIds.length === 0) return;

    const missing = propertyIds.filter((id) => !propertyRegistry[id]);
    if (missing.length === 0) return;
    void k.ensureInventoryIds('Property', missing, { force: true });
  }, [kernel, propertyIds, propertyRegistry]);

  useEffect(() => {
    const propertyIdSet = new Set(propertyIds);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setSelectedPropertyId((prev) => {
      if (focusedPropertyId) return focusedPropertyId;
      // A Portfolio subject with no campaign open and nothing being drafted IS the
      // dashboard. Never keep or restore a lingering asset selection here: it made the
      // header up-arrow land back on the bulk property-leads view (all campaigns mixed,
      // launcher in the right bar) instead of the campaign dashboard.
      //
      // "Nothing being drafted" has to include the campaign that is being prepared RIGHT NOW,
      // or this drops the asset the user picked while its chat session is still being created.
      if (entityType === 'Portfolio' && !sourceSelectedCampaignId && !sourceCreatingCampaign && !sourcePreparingCampaign) {
        return '';
      }
      if (prev && propertyIdSet.has(prev)) return prev;

      const candidates = [
        focusedUnitSourcingSelection.propertyId,
        focusedPropertySourcingSelection.propertyId,
        portfolioSourcingSelection.propertyId,
      ];
      for (const candidate of candidates) {
        if (!candidate) continue;
        if (propertyIdSet.size === 0 || propertyIdSet.has(candidate)) return candidate;
      }
      return '';
    });
  }, [focusedPropertyId, focusedPropertySourcingSelection.propertyId, focusedUnitSourcingSelection.propertyId, portfolioSourcingSelection.propertyId, propertyIds, entityType, sourceSelectedCampaignId, sourceCreatingCampaign, sourcePreparingCampaign]);

  const selectedPropertySourcingSelection = useMemo(() => {
    const selectedPropertyRuntime = selectedPropertyId
      ? (propertyRegistry[selectedPropertyId] as (LeasingPropertyDTO & { sourcingSelection?: SourcingTargetRuntimeState }) | undefined)
      : undefined;
    return normalizeSourcingSelection(selectedPropertyRuntime?.sourcingSelection);
  }, [propertyRegistry, selectedPropertyId]);

  useEffect(() => {
    const focusedUnitId = entityType === 'Unit' ? entityId : '';
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setSelectedUnitId((prev) => {
      if (focusedUnitId) return focusedUnitId;
      if (!selectedPropertyId) return '';

      const prevUnit = prev ? (unitRegistry[prev] as LeasingUnitDTO | undefined) : undefined;
      if (prevUnit?.propertyId === selectedPropertyId) return prev;

      const candidates = [
        focusedUnitSourcingSelection.unitId,
        selectedPropertySourcingSelection.unitId,
        focusedPropertySourcingSelection.unitId,
        portfolioSourcingSelection.unitId,
      ];
      for (const candidate of candidates) {
        if (!candidate) continue;
        const candidateUnit = unitRegistry[candidate] as LeasingUnitDTO | undefined;
        if (candidateUnit?.propertyId === selectedPropertyId) return candidate;
      }
      return '';
    });
  }, [entityId, entityType, focusedPropertySourcingSelection.unitId, focusedUnitSourcingSelection.unitId, portfolioSourcingSelection.unitId, selectedPropertyId, selectedPropertySourcingSelection.unitId, unitRegistry]);

  const selectedProperty = selectedPropertyId
    ? (propertyRegistry[selectedPropertyId] as LeasingPropertyDTO | undefined)
    : undefined;

  const selectedUnit = selectedUnitId
    ? (unitRegistry[selectedUnitId] as LeasingUnitDTO | undefined)
    : undefined;

  // Resolve campaigns from the scope-appropriate registry while keeping a fallback
  // across all campaign families for hydrated-but-not-yet-focused Source Center states.
  // The unified row FIRST: it is the one that is hydrated, and the only one carrying config.name
  // and createdByUserId for every granularity. The family registries stay as fallbacks for states
  // where only they have been loaded.
  const selectedCampaignForScope = selectedCampaignId
    ? (readCampaign(selectedCampaignId) as
        | {
            id?: string;
            createdAt?: string;
            status?: string;
            // `name` is here because the page title reads it and, more importantly, because the
            // title memo has to be able to DEPEND on it. Typing config as recall-only is what
            // hid the rename bug behind a type that could not express what the header needs.
            config?: { recallCommanderOnComplete?: unknown; name?: unknown };
            // The live progress the run broadcasts (completed / total). Declared for the same
            // reason as `name` above: the narration effect has to be able to DEPEND on it.
            metrics?: unknown;
            createdByUserId?: string | null;
          }
        | undefined)
    : undefined;

  // IS THE DASHBOARD WHAT IS ON SCREEN? ONE DEFINITION, BECAUSE THERE WERE TWO AND THEY DISAGREED.
  //
  // The canvas below has exactly two pages (see the branches: a campaign's shortlist, or the
  // dashboard) and it falls through to the dashboard whenever no campaign is open and nothing is
  // being drafted - an asset selection does NOT give it a third page, and the dashboard it renders
  // is portfolio-wide regardless. The header and the right bar answered the same question with
  // "...and no asset is selected", so after the up-arrow left an asset behind they still believed
  // a campaign surface was open: the header read "Asset · Aubergenville" over a portfolio
  // dashboard, and the right bar offered a full "Launch sourcing" form for an asset the user was
  // no longer looking at. Ask the canvas's question, not a stricter one.
  // sourcePreparingCampaign closes the gap between clicking New campaign and the draft flag being
  // raised: during that session round trip nothing is selected and no draft is open, which used to
  // read as "show the dashboard" and flashed it in the user's face on the way in.
  const isShowingDashboard = Boolean(
    isEmptySource
      || !resolvedPortfolioId
      || (!selectedCampaignId && !sourceCreatingCampaign && !sourcePreparingCampaign),
  );

  // A CAMPAIGN IS BEING DRAFTED. ONE DEFINITION, READ BY ALL THREE SURFACES.
  //
  // This existed three times over and the copies disagreed, which is the bug reported twice:
  // clicking "New campaign" on an ASSET gave a header reading "Asset · Bercy Village", a right bar
  // showing the full setup form, and a canvas still drawing the campaign dashboard. Three answers
  // to one question.
  //   - the header asked the asset FIRST, so a draft on an asset was labelled with the asset;
  //   - the portfolio canvas branch asked about the draft (fixed 2026-08-12);
  //   - the asset canvas branch did not ask at all - it fell through to the dashboard on
  //     `!selectedCampaignId` alone, which a draft always satisfies, since a draft HAS no campaign
  //     id yet. That is the one that survived the first fix.
  // Defined here, beside the dashboard rule it is the complement of, so a new surface reads it
  // rather than inventing a fourth copy.
  const isDraftCampaign = Boolean(
    !isEmptySource
      && resolvedPortfolioId
      && !selectedCampaignId
      && (sourceCreatingCampaign || sourcePreparingCampaign),
  );

  // Publish a focus-aware title for the top-bar header. Portal reads this for
  // ORCHESTRATE_SOURCING subjects, so the header tracks whatever the editor is
  // showing: the dashboard, a campaign's fits, leads, or unit fits.
  const sourceHeaderTitle = useMemo(() => {
    // The module's home names the module, like Slides; Shortlist/Assets is the switch beside it.
    if (isShowingDashboard) return 'Source';
    // BEFORE the unit and asset branches, not after. A draft opened from an asset still has that
    // asset selected, so asking the asset first labelled the new campaign with the place it came
    // from - the header said "Asset · Bercy Village" over a setup form for a campaign that did not
    // exist yet. What is on screen is a new campaign; that is what the header must say.
    if (isDraftCampaign) return lang === 'fr' ? 'Nouvelle campagne' : 'New campaign';
    // THE NAME THE USER GAVE IT WINS - and it has to be asked BEFORE the asset, which is what this
    // comment claimed and the ordering denied. Opening an asset campaign always sets
    // selectedPropertyId, so the asset branch below short-circuited every named run: the card read
    // "Toutes catégories · 100-3070 m²" and the header, for the same campaign, read "Asset · Espace
    // Saint Quentin". Two surfaces naming one thing differently, and a rename that saved to the
    // database and appeared to do nothing - the exact symptom this comment was written for.
    const ownName = typeof (selectedCampaignForScope as { config?: { name?: unknown } } | undefined)?.config?.name === 'string'
      ? String((selectedCampaignForScope as { config: { name: unknown } }).config.name).trim()
      : '';
    if (ownName) return ownName;
    // The asset and unit are the CONTEXT of a run that has no name of its own, so they answer after
    // the name, not before it.
    if (selectedUnitId) return `${lang === 'fr' ? 'Unité' : 'Unit'} · ${buildUnitLabel(selectedUnit, selectedUnitId, lang)}`;
    if (selectedPropertyId) return `${lang === 'fr' ? 'Actif' : 'Asset'} · ${selectedProperty?.name?.trim() || selectedPropertyId.slice(0, 8)}`;
    // ONE SOURCING TOOL, THREE GRANULARITIES. A run that names no asset simply covers them all;
    // it is not a different species of thing with a name of its own. "Reference shortlist" was
    // that other species - a single blessed list per portfolio - and it made the product read as
    // two tools where there is one.
    if (scope === 'portfolio' && selectedCampaignId) {
      return lang === 'fr' ? 'Tous les actifs' : 'All assets';
    }
    if (selectedCampaignId) {
      const when = formatSourceDate(selectedCampaignForScope?.createdAt, lang);
      // The date beside it is already localized, so an English "Campaign ·" here produced a
      // half-French, half-English header.
      const campaignWord = lang === 'fr' ? 'Campagne' : 'Campaign';
      return when ? `${campaignWord} · ${when}` : campaignWord;
    }
    if (sourceCreatingCampaign || sourcePreparingCampaign) return lang === 'fr' ? 'Nouvelle campagne' : 'New campaign';
    return lang === 'fr' ? 'Tableau de bord' : 'Dashboard';
    // THE NAME IS A DEPENDENCY. It was not, and that is why renaming "did not work": the write
    // landed in both campaign tables, the registry row updated, and this memo never recomputed
    // because `createdAt` - the only thing it watched on that row - had not changed. The title
    // was correct after a reload and stale before one, which reads exactly like a failed save.
  }, [isShowingDashboard, sourceDashboardTab, isDraftCampaign, scope, selectedUnitId, selectedUnit, selectedPropertyId, selectedProperty?.name, selectedCampaignId, selectedCampaignForScope?.createdAt, selectedCampaignForScope?.config?.name, sourceCreatingCampaign, sourcePreparingCampaign, lang]);

  // The header title is TRANSLATED, so nothing may test it against a literal any more. The one
  // reader that did (SourceSetupPanel's "are we on the dashboard" check) reads this flag instead.
  const sourceHeaderIsDashboard = isShowingDashboard;

  // WHO OWNS THIS SHORTLIST. A property OF the list, in the Notion sense, so it sits under the
  // title rather than in a table column: it describes the whole page, not any one row. The id was
  // already stored and mapped (SourcingCampaign.createdByUserId, "who launched the run") and
  // nothing had ever shown it.
  const shortlistOwnerId = isShowingDashboard
    ? ''
    : String((selectedCampaignForScope as { createdByUserId?: string | null } | undefined)?.createdByUserId ?? '');
  const shortlistOwner = useKernelStore((state) =>
    shortlistOwnerId ? state.inventory.User?.registry?.[shortlistOwnerId] : undefined,
  );
  useEffect(() => {
    // The owner is a user we may never have loaded: the shortlist page can be the first thing a
    // session opens, and a name that arrives only after someone visits the members screen is a
    // name that is usually missing.
    if (!shortlistOwnerId || shortlistOwner) return;
    void kernel.current?.ensureInventoryIds('User', [shortlistOwnerId]).catch(() => undefined);
  }, [kernel, shortlistOwnerId, shortlistOwner]);

  // THE PAGE'S OWN EMOJI. Double-clicking the glyph opens the same picker Doctrine uses; the
  // choice is saved to user preferences, keyed by the campaign, so it survives a reload - which
  // Doctrine's does not.
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  /** Set by Escape so the blur that follows abandons the edit instead of saving it. */
  const renameCancelledRef = useRef(false);
  const [titleDraft, setTitleDraft] = useState('');

  // Renaming writes the campaign's own name (config.name), which the header and the dashboard card
  // both already read when it is set - so one write renames the page and the card together.
  // The route differs by family: an asset run is a SourcingCampaign, the reference shortlist is a
  // PortfolioFitCampaign, and only the first had a PATCH until now.
  const commitTitle = useCallback(async () => {
    const next = titleDraft.trim();
    setIsRenamingTitle(false);
    if (!next || !selectedCampaignId || next === sourceHeaderTitle) return;
    // One rename in the whole app, and it lives in the kernel: components must not fetch /api/*
    // for database data (AGENTS.md 4.1), and this was a second copy that had already drifted from
    // the dashboard's on how it picked the endpoint family.
    await kernel.current?.renameCampaign(selectedCampaignId, next);
  }, [kernel, titleDraft, selectedCampaignId, sourceHeaderTitle]);
  const emojiKey = isShowingDashboard ? 'dashboard' : (selectedCampaignId || 'campaign');
  const pageEmoji = useKernelStore(
    (state) => (state.userPreferences?.sourcePageEmojis ?? {})[emojiKey] ?? '',
  );
  const pickPageEmoji = useCallback(
    (emoji: string | null) => {
      const current = kernel.current?.getState?.()?.userPreferences?.sourcePageEmojis ?? {};
      const next = { ...current };
      if (emoji) next[emojiKey] = emoji;
      else delete next[emojiKey];
      void kernel.current?.updateAndSaveUserPreference('sourcePageEmojis', next);
      setEmojiPickerOpen(false);
    },
    [kernel, emojiKey],
  );

  const shortlistOwnerName = useMemo(() => {
    const owner = shortlistOwner as { firstName?: string | null; lastName?: string | null; email?: string } | undefined;
    if (!owner) return '';
    const full = [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim();
    return full || owner.email || '';
  }, [shortlistOwner]);

  // Dismissed rows are hidden by this switch, not by the table's Status filter, which therefore
  // finds none while it is off (owner, 2026-10-02: 'where is the button to show dismissed
  // brands'). The right bar's copy vanished whenever a brand was open; this one stays with the table.
  const showDismissedInToolbar = useKernelStore((state) => state.sourceShowDismissed);
  const dismissedToggle = (
    <button
      type="button"
      onClick={() => kernel.current?.setState({ sourceShowDismissed: !kernel.current?.getState().sourceShowDismissed })}
      aria-pressed={showDismissedInToolbar}
      className="mr-3 flex h-8 shrink-0 items-center rounded-full bg-white/[0.06] px-3 text-[#E5E5E5] transition-colors duration-150 ease-out hover:bg-white/[0.1]"
      style={{ fontSize: 13 }}
    >
      {showDismissedInToolbar ? (lang === 'fr' ? 'Masquer les écartées' : 'Hide dismissed') : (lang === 'fr' ? 'Voir les écartées' : 'Show dismissed')}
    </button>
  );
  const ownerChip = shortlistOwnerId && shortlistOwnerName ? (
    <div className="mr-3 flex items-center gap-3">
      <span className="shrink-0 text-[#8E8E8E]" style={{ fontSize: 13 }}>
        {lang === 'fr' ? 'Propriétaire' : 'Owner'}
      </span>
      {/* A round chip, as ChatGPT shows a person (owner, 2026-09-28). The shared avatar signs the
          URL and falls back to initials rather than a broken image for an owner with no photo. */}
      <span className="flex h-8 min-w-0 items-center gap-2 rounded-full bg-white/[0.06] pl-1 pr-3">
        <UserAvatar userId={shortlistOwnerId} name={shortlistOwnerName} canEdit={false} size="24px" shape="circle" />
        <span className="truncate text-[#E5E5E5]" style={{ fontSize: 13 }}>{shortlistOwnerName}</span>
      </span>
    </div>
  ) : null;

  // THE MERCH PLAN BUILT ON THIS RUN (owner, 2026-10-14: "sourcings are the basis for merch plan hypotheses"),
  // in its own window: the scenario tied to this campaign when one is, else a new one created on it.
  const merchUser = useKernelStore((state) => state.userContext);
  const merchPlans = useKernelStore((state) => state.pulse.plans);
  const merchPropertyId = selectedCampaignId && !isShowingDashboard
    ? ((selectedCampaignForScope as { propertyIds?: string[] } | undefined)?.propertyIds?.[0] ?? selectedPropertyId) || null
    : null;
  const merchLinked = useMemo(() => Boolean(selectedCampaignId) && Object.values(merchPlans ?? {})
    .some((plan) => plan.campaignId === selectedCampaignId || plan.scenarios?.some((s) => s.campaignId === selectedCampaignId)), [merchPlans, selectedCampaignId]);
  // The link is made in the plan's window: coming back to this one re-reads the plans, so the button says so.
  useEffect(() => {
    if (!merchPropertyId) return;
    const refresh = () => { const k = kernel.current; if (k) void loadPlans(k); };
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [kernel, merchPropertyId]);
  const merchPlanButton = merchPropertyId && selectedCampaignId && canAccessFootfall(merchUser) ? (
    <button
      type="button"
      onClick={() => {
        openMerchPlanWindow(merchPropertyId, selectedCampaignId);
        kernel.current?.logUserAction('Source campaign merch plan opened', { campaignId: selectedCampaignId, propertyId: merchPropertyId, linked: merchLinked });
      }}
      title={merchLinked
        ? (lang === 'fr' ? 'Ouvre, dans une fenêtre, l’hypothèse du plan merch bâtie sur cette recherche' : 'Opens, in a window, the merch plan hypothesis built on this run')
        : (lang === 'fr' ? 'Crée, dans le plan merch de l’actif, une hypothèse bâtie sur cette recherche' : 'Creates, in the asset’s merch plan, a hypothesis built on this run')}
      className="mr-3 flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-white/[0.06] px-3 text-[#E5E5E5] transition-colors duration-150 ease-out hover:bg-white/[0.1]"
      style={{ fontSize: 13 }}
    >
      <MapIcon size={14} />
      <span style={{ fontSize: 'inherit' }}>{merchLinked ? (lang === 'fr' ? 'Hypothèse merch' : 'Merch hypothesis') : (lang === 'fr' ? 'Créer l’hypothèse merch' : 'Create merch hypothesis')}</span>
      <ExternalLink size={12} className="text-[#8E8E8E]" />
    </button>
  ) : null;

  useEffect(() => {
    kernel.current?.setState({ sourceHeaderTitle, sourceHeaderIsDashboard });
    return () => {
      kernel.current?.setState({ sourceHeaderTitle: null, sourceHeaderIsDashboard: false });
    };
  }, [kernel, sourceHeaderTitle, sourceHeaderIsDashboard]);

  const resolvedPropertyIdForData = selectedPropertyId || (selectedUnit?.propertyId ?? '');
  const resolvedUnitIdForData = selectedUnitId || (entityType === 'Unit' ? entityId : '');
  // The asset a unit subject belongs to: with unit sourcing gone, a unit's shortlist IS its
  // asset's, narrowed to the unit.
  const unitPropertyIdForData = resolvedUnitIdForData
    ? ((unitRegistry[resolvedUnitIdForData] as LeasingUnitDTO | undefined)?.propertyId ?? '')
    : '';

  // Does the OPEN run cover several assets? Read off the run's own row rather than the unified
  // projection: this view is reachable without ever loading the campaign list (a conversation
  // restored at boot opens its campaign directly), and the SourcingCampaign row is the one this
  // whole branch already depends on.
  const openCampaignCoversManyAssets = Boolean(
    selectedCampaignId &&
      (readCampaign(selectedCampaignId) as { config?: { propertyIds?: string[] } } | undefined)
        ?.config?.propertyIds?.length,
  );

  const targetFocus = useMemo(() => {
    if (selectedUnitId) {
      return {
        entityType: 'Unit' as const,
        entityId: selectedUnitId,
        label: `${lang === 'fr' ? 'Unité' : 'Unit'} · ${buildUnitLabel(selectedUnit, selectedUnitId, lang)}`,
      };
    }

    if (selectedPropertyId) {
      return {
        entityType: 'Property' as const,
        entityId: selectedPropertyId,
        label: `${lang === 'fr' ? 'Actif' : 'Asset'} · ${selectedProperty?.name || selectedPropertyId.slice(0, 8)}`,
      };
    }

    return {
      entityType: 'Portfolio' as const,
      entityId: resolvedPortfolioId,
      label: `${lang === 'fr' ? 'Portefeuille' : 'Portfolio'} · ${portfolio?.name || resolvedPortfolioId || '–'}`,
    };
  }, [portfolio?.name, resolvedPortfolioId, selectedProperty?.name, selectedPropertyId, selectedUnit, selectedUnitId, lang]);

  // The write itself lives in the kernel (`setSourcingSelection`, kernel/useLeasingWorkflows):
  // same normalize-then-diff-then-patch procedure, one copy.
  useEffect(() => {
    const k = kernel.current;
    if (!k) return;
    const selection = {
      portfolioId: typeof resolvedPortfolioId === 'string' ? resolvedPortfolioId.trim() : '',
      propertyId: typeof selectedPropertyId === 'string' ? selectedPropertyId.trim() : '',
      unitId: typeof selectedUnitId === 'string' ? selectedUnitId.trim() : '',
    };

    if (selection.portfolioId) {
      k.setSourcingSelection('Portfolio', selection.portfolioId, selection);
    }
    if (selection.propertyId) {
      k.setSourcingSelection('Property', selection.propertyId, selection);
    }
    if (selection.unitId) {
      k.setSourcingSelection('Unit', selection.unitId, selection);
    }
  }, [kernel, resolvedPortfolioId, selectedPropertyId, selectedUnitId]);

  // While the open campaign is still working, keep its fits table re-querying so
  // newly created/scored rows fill in (the agent produces them progressively).
  const openCampaign = selectedCampaignForScope;
  // The asset's logo for the header: carried by the unified row, else read off the asset itself.
  const openCampaignRow = openCampaign as { targetLogoUrl?: unknown; targetName?: unknown } | undefined;
  const openCampaignPropertyId = resolveCampaignPropertyId(openCampaign as Parameters<typeof resolveCampaignPropertyId>[0]);
  const openCampaignPropertyLogo = useKernelStore((state) => {
    const logo = openCampaignPropertyId ? (state.inventory.Property.registry[openCampaignPropertyId] as { logoUrl?: unknown } | undefined)?.logoUrl : null;
    return typeof logo === 'string' ? logo : '';
  });
  const openCampaignLogoUrl = (typeof openCampaignRow?.targetLogoUrl === 'string' && openCampaignRow.targetLogoUrl) || openCampaignPropertyLogo || null;
  // A LEAD STILL WORKING OUTRANKS THE CAMPAIGN'S OWN STATUS, WHICH ARRIVES LATE OR NOT AT ALL.
  //
  // Extending a finished shortlist moves the campaign back to WAITING server-side, but nothing
  // pushes that to a page already open: measured mid-extension, the database held WAITING and four
  // PENDING leads while this component still read COMPLETE. Everything below keys off this flag -
  // the reveal gate, the live refresh, and the count of rows still coming - so a stale word here
  // made an entire run invisible, which is exactly the "no loading animation" the user kept
  // reporting. The leads cannot lag: their rows are written before the work starts.
  type OpenCampaignLead = { lastCampaignId?: string; status?: string | null; brandId?: string; brandName?: string | null; brandLogoUrl?: string | null; updatedAt?: string | null };
  const openCampaignLeads = Object.values(
    (kernel.current?.getInventory()?.Lead?.registry ?? {}) as Record<
      string,
      OpenCampaignLead | undefined
    >,
  ).filter(
    (lead): lead is OpenCampaignLead => Boolean(lead) && lead?.lastCampaignId === openCampaign?.id,
  );
  const openCampaignLeadsInFlight = countLeadsInFlight(openCampaignLeads);

  // Live run progress off the campaign DTO's metrics (updated via socket snapshots): drives
  // the "reviewer at work" state so an empty gated table NEVER reads as "nothing happened".
  const openCampaignProgress = useMemo(() => {
    const metrics = (openCampaign as {
      metrics?: {
        progress?: { completed?: unknown; total?: unknown };
      };
    } | undefined)?.metrics;
    const progress = metrics?.progress;
    const total = typeof progress?.total === 'number' && Number.isFinite(progress.total) ? progress.total : null;
    const completed = typeof progress?.completed === 'number' && Number.isFinite(progress.completed) ? progress.completed : null;
    return {
      total,
      completed,
    };
  }, [openCampaign]);

  // WHAT THE RUN IS DOING, taken from the rows rather than from the worker's report. The leads of
  // a pass are written before it starts, so they answer "how many, and which ones" a full minute
  // before `metrics.progress` exists. The campaign's own numbers stay as the fallback, because a
  // re-run reuses rows this list would otherwise count twice.
  const waitingTotal = openCampaignProgress.total ?? (openCampaignLeads.length || null);
  const waitingDone =
    openCampaignProgress.completed ??
    Math.max(0, openCampaignLeads.length - openCampaignLeadsInFlight);
  // A ROW STILL SAYING "RUNNING" IS NOT PROOF THAT ANYTHING IS RUNNING.
  //
  // Reported 2026-08-19: an extend of two brands announced "Analysing Toscane", a third brand
  // nobody asked for. The database was right - Toscane had settled INTERESTING at 08:06 - and the
  // browser was wrong: it still held the row as RUNNING from that earlier pass, because a status
  // that changes while the page is elsewhere only arrives if its event does. The narration trusted
  // that copy and named a brand whose analysis had ended an hour before.
  //
  // So a row has to be BOTH in flight and fresh to be spoken about. A pass claims its rows when it
  // starts on them, so a genuine one is touched within minutes; anything older is a leftover, and
  // silence about it is better than a sentence that invents work.
  // Reading the clock at render is the point: staleness is measured against NOW, not against
  // anything in props or state, so there is nothing pure to derive it from. Same exemption and
  // same reason as the one above, and the established pattern in this codebase is to exempt and
  // say why rather than fake a dependency.
  // eslint-disable-next-line react-hooks/purity
  const inFlightSince = Date.now() - 10 * 60 * 1000;
  const freshlyInFlight = openCampaignLeads.filter((lead) => {
    if (!isLeadInFlight(lead.status)) return false;
    const touched = typeof lead.updatedAt === 'string' ? Date.parse(lead.updatedAt) : NaN;
    return Number.isNaN(touched) ? true : touched >= inFlightSince;
  });
  const waitingNames = freshlyInFlight
    .map((lead) => {
      // The lead carries the name of its brand precisely so a list can be drawn without a second
      // fetch. Reading the Brand registry FIRST and stopping there printed nothing at all on a
      // fresh run, measured on the production build: the leads arrive before their brands are
      // hydrated, which is the exact minute this line exists to fill.
      if (typeof lead.brandName === 'string' && lead.brandName.trim().length > 0) return lead.brandName.trim();
      const brand = lead.brandId
        ? ((kernel.current?.getInventory()?.Brand?.registry ?? {}) as Record<string, { name?: string } | undefined>)[lead.brandId]
        : undefined;
      return typeof brand?.name === 'string' ? brand.name : '';
    })
    .filter((name) => name.length > 0)
    .slice(0, 3)
    .join(', ');
  // The brands picked for this run, as the logo carousel the chat shows until the first one lands:
  // the ones being analysed now first and lit. A string, so the narration effect re-runs only when
  // the carousel itself changes.
  // The worker's research stage, when this run is in it (lib/leasing/researchStage.ts).
  const openResearch = activeCampaignResearch(openCampaign as Parameters<typeof activeCampaignResearch>[0]);
  const openSelection = campaignSelection(openCampaign as Parameters<typeof campaignSelection>[0]);
  const choosingKey = openSelection?.choosing ? `choosing:${openSelection.total}/${openSelection.eligible}` : '';
  // The pick's own carousel: every candidate while it chooses, then the chosen ones lit among them
  // until the research takes over with the chosen ones alone (owner, 2026-09-27).
  const candidateCarouselJson = openSelection && openSelection.candidates.length > 0
    ? JSON.stringify(openSelection.candidates.map((c) => ({
        name: c.name,
        logoUrl: c.logoUrl,
        active: !openSelection.choosing && openSelection.pickedIds.includes(c.id),
      })))
    : '';
  const researchCurrentKey = openResearch ? `${openResearch.completed}/${openResearch.total}:${openResearch.current.join(',')}` : '';
  const carouselJson = JSON.stringify(
    openCampaignLeads
      .filter((lead) => typeof lead.brandName === 'string' && lead.brandName.trim().length > 0)
      .map((lead) => ({
        name: String(lead.brandName).trim(),
        logoUrl: lead.brandLogoUrl
          ?? (lead.brandId ? ((kernel.current?.getInventory()?.Brand?.registry ?? {}) as Record<string, { logoUrl?: string | null } | undefined>)[lead.brandId]?.logoUrl : null)
          ?? null,
        active: freshlyInFlight.includes(lead) || Boolean(lead.brandId && openResearch?.current.includes(lead.brandId)),
      }))
      .sort((a, b) => Number(b.active) - Number(a.active))
      .slice(0, 40),
  );
  // THE CAMPAIGN ROW DECIDES once it has a status: it is re-read every 8 s (below), while a lead
  // whose final update was missed stays "in flight" in this browser forever. That froze a finished
  // run as live for good - "Source is working", Stop the run, a mute chat - on preprod 2026-09-26.
  // Leads still count while the row has no status yet, which is the window they exist for.
  const openCampaignStatus = String(openCampaign?.status ?? '').toUpperCase();
  const isOpenCampaignActive = Boolean(
    openCampaign &&
      ((ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(openCampaignStatus) ||
        (!openCampaignStatus && openCampaignLeadsInFlight > 0))
  );

  // ── The agent narrates the run while it happens ──────────────────────────────────────────────
  // The landing turn below is the agent's VERDICT. This is the commentary before it, and without it
  // a run launched from the right panel was silent: the figures moved, the ranking filled in, and
  // the agent said nothing until the end, so the panel looked like it was doing the work by itself.
  //
  // Written through setSessionStatus, which is the SAME telemetry line the agent uses for its own
  // steps - Chat.tsx renders chatState in the message stack with the last line animated - so a
  // panel launch is indistinguishable from the agent acting on its own initiative. That is the whole
  // point: one voice, whatever pressed the button.
  //
  // Milestones, not ticks: a line per brand is a scroll, not a narration. And never while the agent
  // is mid-turn, because it writes its own steps into this same list and the two would interleave.
  const narratedRunRef = useRef<string>('');
  const narratedCarouselRef = useRef<string>('');
  useEffect(() => {
    const k = kernel.current;
    if (!k) return;
    const sessionId = k.getActiveChatSessionId();
    if (!sessionId) return;
    if (k.readEntityRuntimeState('ChatSession', sessionId)?.runningScriptId) return;

    if (!isOpenCampaignActive || !openCampaign) {
      // The run is over: hand the floor back, so the landing turn below is not preceded by a stale
      // "scoring 40 of 50" that never resolved.
      //
      // UNLESS THE PASS WAS CUT SHORT, and then silence is the defect. A run that spends its credit
      // budget at brand 29 of 50 abandons the other 21 and still settles COMPLETE, so clearing the
      // line left "Scored 29 of 50" looking like a counter that had frozen: the user waits, decides
      // it is stuck, and relaunches a run that bills again. Measured on cmsuod81s000hxqt0f03afdag,
      // 50 rows, 29 scored, 21 left PENDING, 50 credits spent. This is the one case where the
      // commentary has something the verdict does not: the verdict ranks what was scored, it does
      // not know what was never looked at.
      // THE LAST LINE STAYS, AND IT SAYS WHAT THE RUN DID. Clearing it was wrong twice over: the
      // strip went blank the moment a pass finished, so the user lost the count they had been
      // watching, and a pass cut short by its budget said nothing at all about the brands it never
      // reached. Both reported on 2026-08-16.
      //
      // Written whether or not THIS page-load produced the earlier lines: the ref resets on every
      // mount, so a run that ended while the user was elsewhere used to keep its running line
      // forever, and the strip animates the last line, which read as work still in progress.
      narratedRunRef.current = '';
      const endProgress = (openCampaign?.metrics as
        | { progress?: { completed?: unknown; total?: unknown; truncatedReason?: unknown } }
        | undefined)?.progress;
      const endCompleted = typeof endProgress?.completed === 'number' ? endProgress.completed : 0;
      const endTotal = typeof endProgress?.total === 'number' ? endProgress.total : 0;
      // A pass that stopped short says so; one that covered its list keeps its final count. Never
      // an empty string: that wipes the whole strip, including the agent's own steps.
      const finalLine =
        resolveRunTruncationLine(endCompleted, endTotal, endProgress?.truncatedReason as SourcingTruncationReason | undefined, lang)
        ?? (endTotal > 0 ? resolveRunNarrationLine(endCompleted, endTotal, lang) : '');
      // `settled`, or the strip keeps animating this line and a finished pass reads as one still
      // running: "Scored 10 of 10 brands" pulsing for good was reported the same day this line was
      // made to persist.
      // ONCE PER RUN. `replacesPrevious` swaps the LAST strip entry whatever it is, and this effect
      // re-fires on every campaign or lead update - including the ones that arrive after the landing
      // turn has been woken and has written "Thinking". The second write replaced that, every entry
      // left was settled, `agentIsWorking` went false, and the composer offered to send on top of a
      // streaming answer. Caught in review before merge.
      if (finalLine && settledRunRef.current !== selectedCampaignId) {
        settledRunRef.current = selectedCampaignId;
        k.setSessionStatus(sessionId, finalLine, { replacesPrevious: true, settled: true });
      }
      return;
    }

    // OFF THE ROWS, NOT OFF THE CAMPAIGN. Measured 2026-08-18 on a ten-brand run: launched 21:47:38,
    // every verdict landed between 21:49:01 and 21:49:41, and `metrics.progress` was absent for the
    // whole two minutes in between. Narrating from it meant the chat said nothing while ten brands
    // were being researched, then everything at once. The lead rows are written before the work
    // starts and flip as each brand is claimed, so they can say what is happening while it happens.
    const progress = (openCampaign.metrics as { progress?: { completed?: unknown; total?: unknown } } | undefined)?.progress;
    const metricsCompleted = typeof progress?.completed === 'number' ? progress.completed : 0;
    const metricsTotal = typeof progress?.total === 'number' && progress.total > 0 ? progress.total : 0;
    const completed = Math.max(metricsCompleted, waitingDone);
    const total = metricsTotal > 0 ? metricsTotal : (waitingTotal ?? 0);
    // ONE LINE THAT MOVES WITH EVERY BRAND, naming the ones being researched. Quarter milestones
    // left a 50-brand pass silent for minutes (reported 2026-09-26 on app.fater.ai: the run was
    // fine, the chat looked stalled). The line replaces itself, so it never becomes a scroll.
    // Queued, the campaign still carries the PREVIOUS pass's progress: an extend read "Scored 10 of
    // 10 brands" for 80 seconds before its first brand was claimed (preprod, 2026-09-26).
    const queued = ['PENDING', 'WAITING'].includes(String(openCampaign.status ?? '').toUpperCase());
    // During research the line says which brands are being researched and how far it is.
    // Named from the rows when they have reached the page, else from the pick's own candidate
    // list, else from the brand registry: the worker writes the rows, and for the first seconds
    // of research the page has not fetched them yet ("Researching the brands…", 2026-09-27).
    const brandNameOf = (brandId: string): string =>
      openCampaignLeads.find((lead) => lead.brandId === brandId)?.brandName?.trim()
      || openSelection?.candidates.find((candidate) => candidate.id === brandId)?.name
      || ((k.getInventory()?.Brand?.registry ?? {}) as Record<string, { name?: string } | undefined>)[brandId]?.name
      || '';
    const researchNames = openResearch
      ? (() => {
          const names = openResearch.current.map(brandNameOf).filter((name) => name.length > 0);
          return names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
        })()
      : '';
    // ONE VERB PER PHASE, the brand named (owner, 2026-09-27): choosing, researching, contemplating.
    const line = openSelection?.choosing
      ? (lang === 'fr'
        ? `Choix de ${openSelection.total} marques parmi ${openSelection.eligible.toLocaleString('fr-FR')} candidates…`
        : `Choosing ${openSelection.total} brands among ${openSelection.eligible.toLocaleString('en-US')} candidates…`)
      : openResearch
      ? (lang === 'fr'
        ? `Recherche sur ${researchNames || 'les marques'}… (${openResearch.completed}/${openResearch.total})`
        : `Researching ${researchNames || 'the brands'}… (${openResearch.completed}/${openResearch.total})`)
      : queued
      ? resolveRunNarrationLine(0, 0, lang)
      : completed === 0 && waitingNames.length > 0
      ? (lang === 'fr' ? `Réflexion sur ${waitingNames}…` : `Contemplating ${waitingNames}…`)
      : resolveRunNarrationLine(completed, total, lang, waitingNames);
    // The agent's own live step outranks ours. When its turn ends it wipes the strip, and the next
    // campaign update writes the count again: the first line used to be lost that way and never
    // come back, because a milestone already spoken was never repeated.
    const liveLine = (k.readEntityRuntimeState('ChatSession', sessionId)?.chatState ?? [])
      .filter((entry) => entry.settled !== true)
      .at(-1)?.text;
    // Until the first brand lands, the line is drawn as the carousel of the brands being analysed;
    // before that, of the candidates the pick is choosing from, the chosen ones lit once it has.
    const showCandidates = candidateCarouselJson && !openResearch && (openSelection?.choosing || completed === 0);
    const carousel = showCandidates ? candidateCarouselJson : queued || completed === 0 ? carouselJson : '[]';
    if (liveLine && liveLine !== narratedRunRef.current) return;
    if (liveLine === line && carousel === narratedCarouselRef.current) return;
    narratedRunRef.current = line;
    narratedCarouselRef.current = carousel;
    k.setSessionStatus(sessionId, line, { ambient: true, brands: JSON.parse(carousel) });
  }, [kernel, isOpenCampaignActive, openCampaign, lang, waitingDone, waitingNames, waitingTotal, carouselJson, researchCurrentKey, choosingKey, candidateCarouselJson]);

  // ── The agent speaks when the run lands ──────────────────────────────────────────────────────
  // Replaces the canned "Launched a sourcing campaign" line that used to be posted the moment you
  // pressed the button, a fixed string, written before anything had happened, pretending to be
  // the agent. The pertinent moment is not the launch, it is the LANDING: that is when there is a
  // ranking to read and something worth saying about it.
  //
  // `recallCommanderOnComplete` has been carried on the launch payload, stored in the campaign
  // config and echoed in the proposal card since the feature was designed, and consumed by
  // nothing. This is the consumer.
  //
  // Strictly once per campaign, and only for the campaign on screen: an agent turn costs tokens,
  // and one that fires twice reads as a stutter. `triggerReEvaluation` is used rather than
  // `execute` because there is no user prompt to show, the agent simply picks the conversation
  // back up, which is what makes it read as cognition instead of narration.
  // Campaigns this browser session has SEEN running. This is the licence for any spontaneous
  // agent activity about a campaign: watched it run here, or it does not get to speak here.
  // THE OPEN CAMPAIGN'S ROW HAS TO BE RE-READ, OR THE VIEW CANNOT NOTICE A RUN THAT STARTED.
  //
  // Everything that says "work is happening" - the reveal gate, the rows still coming, the tables'
  // own liveRefresh - hangs off this campaign's status. Nothing pushed a status change to a page
  // that was already open, and the leads that would have betrayed the run are only re-queried WHEN
  // the view already believes it is running. That is a closed loop: extending a shortlist from an
  // open page could never light anything up, which is the bug the user reported three times.
  // One row, every eight seconds, only while a campaign is on screen, breaks it.
  //
  // AND IT HAS TO GIVE UP. A session whose campaign has since been deleted kept this timer firing
  // against a row that will never come back: observed 404ing every 8 seconds, forever, on a page
  // left open. Three consecutive failures is the stop - one null is a network blip and stopping on
  // it would kill live updates until the next remount, three in a row is a campaign that is gone.

  useEffect(() => {
    const k = kernel.current;
    if (!k || !selectedCampaignId) return;
    // The unified row, because it is the one readCampaignRow reads first: syncing the legacy name
    // refreshed a copy the page never looked at once the unified row was hydrated. FORCED, because
    // once a row's detail coverage is full syncEntity answers from the cache without a request:
    // measured on the preview, one fetch after the launch and then nothing, so a finished run kept
    // its "Source is working" glow until a reload (2026-09-27).
    let misses = 0;
    const timer = setInterval(() => {
      void kernel.current?.syncEntity('Campaign', selectedCampaignId, { force: true }).then((row) => {
        misses = row ? 0 : misses + 1;
        if (misses >= 3) clearInterval(timer);
      });
    }, 8000);
    return () => clearInterval(timer);
  }, [kernel, selectedCampaignId]);

  // Declared before the narration effect below because that effect is its most important
  // consumer.
  const witnessedActiveCampaignsRef = React.useRef<Set<string>>(new Set());
  const narratedCampaignsRef = useRef<Set<string>>(new Set());
  /** The campaign whose final count has already been written. See the settle branch below. */
  const settledRunRef = useRef<string | null>(null);

  useEffect(() => {
    const k = kernel.current;
    if (!k || !selectedCampaignId || !openCampaign) return;
    const sessionId = k.getActiveChatSessionId();
    // The decision lives in lib/leasing/source-view-guards (tested there). The load-bearing
    // rule: ONLY a landing this browser session actually WATCHED may wake the agent. Without
    // it, merely OPENING an old finished campaign started a full unprompted agent turn on
    // every visit - the agent "resumed" work nobody asked for, moved the user's focus around,
    // and spent tokens.
    const mayRecall = shouldRecallDealerOnLanding({
      status: typeof openCampaign.status === 'string' ? openCampaign.status : '',
      recallWanted: openCampaign.config?.recallCommanderOnComplete,
      witnessed: witnessedActiveCampaignsRef.current.has(selectedCampaignId),
      alreadyNarrated: narratedCampaignsRef.current.has(selectedCampaignId),
      hasSession: Boolean(sessionId),
    });
    if (!mayRecall || !sessionId) return;
    narratedCampaignsRef.current.add(selectedCampaignId);
    void k.triggerReEvaluation?.('commander_workspace', undefined, sessionId);
  }, [kernel, selectedCampaignId, openCampaign, openCampaign?.status]);

  // The aura used to mean only "a sourcing pass is running". It now also means "the agent is
  // working on this shortlist", a /refine goal loop or a bulk enrichment can run for many turns
  // and several minutes, and with no ambient signal the surface looked idle while it was rewriting
  // the rubric under the user. Same two signals the composer's stop button already treats as
  // activity, so the pulse and the stop affordance can never disagree.
  const showWorkingAura =
    isOpenCampaignActive || (sourceEnrichInFlight ?? 0) > 0 || isRescoringShortlist;

  // The open campaign is re-analysing NAMED brands (right bar, "Run analysis again" on one brand or
  // on a row selection) rather than building a shortlist. It borrows the same campaign lifecycle,
  // so without this the whole table would fall back into the progressive-reveal state below: every
  // non-SUCCESS row of a finished run would disappear for the length of the pass, including the
  // rows being re-analysed, which are exactly the rows the user is watching.
  //
  // Read through the shared marker reader so the list shape and the single-brand shape it replaced
  // are both recognised; a marker written by the previous shape can still be on a live row.
  const isBrandRerun = Boolean(
    isOpenCampaignActive &&
      readBrandRerunMarker((openCampaign as { config?: unknown } | undefined)?.config)
  );
  // The reveal gate answers "is this shortlist still being BUILT", which a re-run is not.
  const isOpenCampaignBuilding = isOpenCampaignActive && !isBrandRerun;
  // How many brands this run still owes the table: everything it was told to analyse, minus the
  // rows already scored and on screen. Zero unless a pass is actually running, so a settled
  // shortlist never grows a phantom tail.



  // When the watched campaign completes, document the reviewer's work in the left chat ,
  // the same telemetry rail every agent uses. Once per campaign.
  useEffect(() => {
    if (isOpenCampaignActive && typeof selectedCampaignId === 'string' && selectedCampaignId.length > 0) {
      witnessedActiveCampaignsRef.current.add(selectedCampaignId);
      // A SECOND RUN ON THE SAME SHORTLIST DESERVES ITS OWN VERDICT. "Narrated" was keyed on the
      // campaign, which is right for a campaign that runs once and wrong for the one thing that
      // makes a shortlist grow: extending REUSES the campaign, so the landing turn was suppressed
      // by the verdict given when the original run finished. The user asked for one brand that
      // could beat FitnessBoutique, got "Adding 1 brand", and never heard whether it did.
      //
      // Seeing the campaign go active again IS the new run, so that is where the flag is cleared.
      narratedCampaignsRef.current.delete(selectedCampaignId);
      // Same reason: a second run on this shortlist gets its own final count. Exempted like the
      // delete above it: both are refs, which is precisely the box you are meant to mutate without
      // a render, and the compiler cannot see that `.current` on a ref is not a value it owns. The
      // established pattern in this codebase (kernel/useAgents.ts) is to exempt and say why.
      // eslint-disable-next-line react-hooks/immutability
      settledRunRef.current = null;
    }
  }, [isOpenCampaignActive, selectedCampaignId]);



  // Shown while the open campaign runs and no lead has cleared the full process yet: the
  // shortlist is deliberately withheld (scored + reviewed only), so say so, out loud, with
  // live progress, instead of the default "No results found." dead end.
  // ROWS THAT ARE COMING, drawn as skeletons under the ones that are here.
  //
  // The table only reveals a lead once it is FULLY scored - deliberately, because a half-scored
  // shell reads as a real row with wrong numbers. That is right for a first run, where the table is
  // empty and `reviewingEmptyState` explains the wait. It is wrong for an EXTENSION: the list
  // already has rows, so the empty state never shows, and ten new brands being analysed look
  // exactly like nothing happening.
  //
  // So: one skeleton per brand still being worked on. It is honest (no invented numbers), it is
  // countable (you can see how many are coming), and it disappears as each real row lands.
  const reviewingEmptyState = useMemo(
    () => (
      <div className="flex flex-col items-center justify-center gap-3 p-14 text-center">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#2A2A2A] border-t-white/70" aria-hidden />
        <div className="text-sm font-medium text-white/85">
          {lang === 'fr' ? 'Source travaille' : 'Source is working'}
        </div>
        <div className="text-xs text-[#6E6E6E]">
          {openSelection?.choosing
            ? (lang === 'fr' ? 'Choix des marques à analyser pour cet actif.' : 'Choosing the brands to analyse for this asset.')
            : (lang === 'fr' ? 'Les marques apparaissent dès leur sélection.' : 'Brands appear as soon as they are picked.')}
        </div>
      </div>
    ),
    [lang, openSelection?.choosing]
  );

  const realSelectedCampaignId =
    typeof selectedCampaignId === 'string' &&
    selectedCampaignId.length > 0 &&
    selectedCampaignId !== SOURCE_DRAFT_CAMPAIGN_ID
      ? selectedCampaignId
      : '';
  const assetReviewCampaignId = scope === 'asset' ? realSelectedCampaignId : '';

  const portfolioReviewCampaignId = scope === 'portfolio' ? realSelectedCampaignId : '';

  const assetReviewMode = useCampaignTableReviewMode({
    campaignType: 'SourcingCampaign',
    campaignId: assetReviewCampaignId,
    entityType: 'Lead',
    reviewFilters: { lastCampaignId: assetReviewCampaignId },
    filePrefix: 'fater_sourcing_campaign',
    buildSnapshot: (lead: LeasingLeadDTO) => ({
      brandName: lead.brandName || '',
      score: typeof lead.fitScore === 'number' && Number.isFinite(lead.fitScore) ? lead.fitScore : '',
      initialStatus: lead.status || '',
    }),
  });


  const portfolioReviewMode = useCampaignTableReviewMode({
    campaignType: 'PortfolioFitCampaign',
    campaignId: portfolioReviewCampaignId,
    entityType: 'PortfolioFit',
    reviewFilters: { portfolioId: resolvedPortfolioId, campaignId: portfolioReviewCampaignId },
    filePrefix: 'fater_portfolio_fit_campaign',
    buildSnapshot: (fit: PortfolioFitDTO) => ({
      brandName: fit.brandName || '',
      score: typeof fit.score === 'number' && Number.isFinite(fit.score) ? fit.score : '',
      initialStatus: fit.status || '',
    }),
  });

  // ── Stable props for the memoized tables ─────────────────────────────────────────────────────
  // Everything below is handed to LeadsTable / PortfolioFitsTable, which are React.memo'd. Built
  // once per change of their inputs rather than inline in the JSX, so a socket snapshot of a
  // running campaign no longer re-renders the entire shortlist through a changed prop identity.
  const handleControlledSelectionChange = useCallback(
    (ids: string[]) => {
      kernel.current?.setState({ sourceSelectedFitIds: ids });
    },
    [kernel]
  );

  const portfolioReviewToolbarHeader = useMemo(
    () => (portfolioReviewCampaignId ? () => portfolioReviewMode.controls : undefined),
    [portfolioReviewCampaignId, portfolioReviewMode.controls]
  );

  const assetReviewToolbarHeader = useMemo(
    () => (assetReviewCampaignId ? () => assetReviewMode.controls : undefined),
    [assetReviewCampaignId, assetReviewMode.controls]
  );

  const referenceLockedFilters = useMemo(
    () => ({ portfolioId: resolvedPortfolioId }),
    [resolvedPortfolioId]
  );

  const campaignFilterId = selectedCampaignId ?? SOURCE_DRAFT_CAMPAIGN_ID;

  const fitsLockedFilters = useMemo(
    () => ({ portfolioId: resolvedPortfolioId, campaignId: campaignFilterId }),
    [resolvedPortfolioId, campaignFilterId]
  );

  const campaignLeadsLockedFilters = useMemo(
    () => ({ lastCampaignId: campaignFilterId }),
    [campaignFilterId]
  );

  // Progressive reveal, same contract as the Portfolio-subject campaign branch: while
  // the open campaign is running, only fully scored + vetted leads (terminal SUCCESS)
  // surface. This branch renders for a PROPERTY subject, the state a fresh launch
  // lands in, so it needs the same gate.
  //
  // The asset lock is dropped for a run that covers SEVERAL assets. `lastCampaignId` already
  // names exactly this run's leads, and intersecting it with the subject's asset would show a
  // third of a three-asset shortlist while the card next to it says "3 assets".
  //
  // A DRAFT FILTERS ON THE SENTINEL, exactly as the portfolio branch does (`campaignFilterId`).
  // `?? undefined` drops the key from the filter entirely, so a campaign being drafted - which has
  // no id yet - locked on nothing and listed every lead the asset had ever produced: the merged,
  // mixed-rubric view that noBulkLeadList.test.ts exists to keep buried. The sentinel matches no
  // row, so the draft gets the empty shell it should have.
  const assetLeadsLockedFilters = useMemo<Partial<LeasingLeadFilters>>(
    () => ({
      ...(openCampaignCoversManyAssets ? {} : { propertyId: resolvedPropertyIdForData }),
      lastCampaignId: campaignFilterId,
    }),
    [openCampaignCoversManyAssets, resolvedPropertyIdForData, campaignFilterId]
  );

  // The reveal gate of the campaign shortlist (Portfolio subject). Kept as its own value rather
  // than shared with the asset one below: shortlistRevealGate.test.ts checks that BOTH lead tables
  // name the shared rule, because the copy on the asset table survived the first fix.
  // COMPOSED WITH THE REVEAL GATE, not instead of it. The gate answers "is this row finished"; the
  // toggle answers "does the reader want to see a verdict of no". Both have to hold.
  const showDismissed = useKernelStore((state) => state.sourceShowDismissed);
  const campaignLeadsRowFilter = useMemo(
    () => (lead: { status?: string | null }) => {
      if (isOpenCampaignBuilding && !isLeadRevealedWhileBuilding(lead?.status)) return false;
      if (!showDismissed && String(lead?.status ?? '').toUpperCase() === 'DISMISSED') return false;
      return true;
    },
    [isOpenCampaignBuilding, showDismissed]
  );

  // Same reveal gate as the campaign shortlist above, and the same correction: a SUCCESS-only
  // lock hides the curated rows too, so the list shrinks while a pass adds to it. Held on the
  // client (REVEALED_WHILE_BUILDING) rather than as a server filter, because the server
  // matches status by equality and cannot express "SUCCESS or a verdict".
  // The asset table is the one an asset campaign renders, and it needs the SAME two rules as the
  // campaign table above. Patching only the other one was measured on 2026-08-16: the toggle
  // flipped its label and not a single row moved.
  const assetLeadsRowFilter = useMemo(
    () => (lead: { status?: string | null }) => {
      if (isOpenCampaignBuilding && !isLeadRevealedWhileBuilding(lead?.status)) return false;
      if (!showDismissed && String(lead?.status ?? '').toUpperCase() === 'DISMISSED') return false;
      return true;
    },
    [isOpenCampaignBuilding, showDismissed]
  );

  // A NEW CAMPAIGN ON A UNIT SHOWED OLD CAMPAIGNS (owner, 2026-10-02: 'launching new campaigns
  // weirdly opens an existing campaign'). The unit view listed every lead of the unit's asset, all
  // runs mixed, so a draft opened from a unit showed the previous runs' shortlist under 'New
  // campaign' until the new run replaced it. It now answers the asset view's question the same way:
  // a draft or an open campaign shows that campaign's leads only (the draft sentinel matches none);
  // with neither, the unit still shows its asset's leads narrowed to it.
  const unitCampaignScoped = Boolean(selectedCampaignId) || sourceCreatingCampaign || sourcePreparingCampaign;
  const unitLeadsLockedFilters = useMemo(
    () => ({ propertyId: unitPropertyIdForData, ...(unitCampaignScoped ? { lastCampaignId: campaignFilterId } : {}) }),
    [unitPropertyIdForData, unitCampaignScoped, campaignFilterId]
  );

  const renderSourcingData = () => {
    if (isShowingDashboard && sourceDashboardTab === 'assets') return <SourceAssetsDashboard />;
    if (targetFocus.entityType === 'Portfolio') {
      // THERE IS NO PORTFOLIO TO PICK (owner, 2026-09-30: 'there is no notion of portfolio
      // selection'). With nothing of Source's open (for instance Source reached from an Analyst
      // conversation, whose subject is no portfolio), the answer is the campaigns dashboard, which
      // lists every campaign whatever the portfolio. It used to be a dead end asking the user to
      // choose a portfolio in a panel that no longer offers one. A draft or an open campaign
      // carries on below.
      if (!resolvedPortfolioId && !selectedCampaignId && !isDraftCampaign) {
        return (
          <div className="flex flex-col pb-10">
            <CampaignDashboard portfolioId="" onSelect={setSelectedCampaignId} />
          </div>
        );
      }
      // Draft = "New campaign" clicked but not yet launched: show the fits table empty (sentinel
      // id) so the user sees the campaign shell that will fill in. `isDraftCampaign` is defined
      // once, up beside isShowingDashboard - it is deliberately NOT recomputed here.
      //
      // No campaign open and not drafting → show the Source dashboard: the campaign
      // dashboard on top, then compact previews of the inventory tables (which no
      // longer live in the left nav), each with a button into the full table.
      // A whole-portfolio run IS the single reference shortlist. Show the ACCUMULATED list (all
      // fits for the portfolio, across every update run), never a single campaign's slice, so
      // updating complements/enlarges the same living list.
      if (scope === 'portfolio' && selectedCampaignId) {
        const portfolioReviewActive = Boolean(portfolioReviewCampaignId && portfolioReviewMode.isActive);
        return (
          <div className="flex h-full flex-col">
            <div className="min-h-0 flex-1">
              <PortfolioFitsTable
                key={`reference-${resolvedPortfolioId}`}
                scopedIds={portfolioReviewActive ? portfolioReviewMode.reviewIds : undefined}
                initialFilters={portfolioReviewActive ? REVIEW_MODE_INITIAL_FILTERS : FIT_SCORE_SORT}
                lockedFilters={referenceLockedFilters}
                assetLedgerTitle={lang === 'fr' ? 'Shortlist de référence' : 'Reference shortlist'}
                assetLedgerBreadcrumb={lang === 'fr' ? 'Sourcing / Shortlist de référence' : 'Sourcing / Reference shortlist'}
                viewModeVisual={viewMode}
                onViewModeVisualChange={setViewMode}
                inlineExpand
                onExpandedIdChange={setFocusedFit}
                expandedId={focusedFitId}
                liveRefresh={isOpenCampaignActive}
                controlledSelectionIds={sourceSelectedFitIds}
                onControlledSelectionChange={handleControlledSelectionChange}
                renderCustomToolbarHeader={portfolioReviewToolbarHeader}
                toolbarContextMenuActions={portfolioReviewCampaignId ? portfolioReviewMode.contextMenuActions : undefined}
              />
            </div>
          </div>
        );
      }

      if (!selectedCampaignId && !isDraftCampaign) {
        return (
          <div className="flex flex-col pb-10">
            {/* No Database folder row. Assets / Tenants / Contacts opened raw inventory tables
                straight off the dashboard, which is a door OUT of the work: the dashboard is
                campaigns, and a shortlist is what the user came for. The tables themselves are
                untouched and still reachable from the Tables tool. */}
            <CampaignDashboard portfolioId={resolvedPortfolioId} onSelect={setSelectedCampaignId} />
          </div>
        );
      }

      // A campaign is open (or being drafted) → show its brand fits. The table is
      // keyed by the campaign id so launching remounts it onto the real campaign,
      // which then populates progressively as the agent scores brands.
      const assetReviewActive = Boolean(assetReviewCampaignId && assetReviewMode.isActive);
      const portfolioReviewActive = Boolean(portfolioReviewCampaignId && portfolioReviewMode.isActive);
      return (
        <div className="flex h-full flex-col">
          {/* No inline back link. The header's up-arrow is the ONE way out of a campaign, and it
              does exactly the same thing: closes the campaign and hands the chat back to the
              dashboard conversation. A second affordance for one action is noise, and two of them
              can drift apart. */}
          <div className="min-h-0 flex-1">
            {scope === 'asset' ? (
              <>
              <LeadsTable
                key={campaignFilterId}
                glass
                toolbarLead={<>{merchPlanButton}{ownerChip}</>}
                scopedIds={assetReviewActive ? assetReviewMode.reviewIds : undefined}
                initialFilters={assetReviewActive ? REVIEW_MODE_INITIAL_FILTERS : LEAD_SCORE_SORT}
                // Progressive reveal, gated on the WHOLE process: while the campaign is
                // running, only leads that finished scoring + vetting (terminal SUCCESS)
                // surface, brands pop in one by one as each clears the reviewer, never a
                // half-processed row. Once the campaign ends the lock drops so the full
                // outcome (including dismissed/failed rows) is inspectable.
                // No status lock on the server: the reveal gate is the client filter below, which
                // needs to see the curated rows in order to keep them. The scope is one campaign's
                // leads either way, so nothing is over-fetched.
                lockedFilters={campaignLeadsLockedFilters}
                // THE REVEAL GATE HIDES WORK IN PROGRESS. IT MUST NOT HIDE A DECISION.
                //
                // It used to admit `status === 'SUCCESS'` and nothing else, so the moment a run
                // started every curated row vanished from the table: extending a shortlist made a
                // brand the agent had just marked INTERESTING disappear, and the list the user was
                // reading got SHORTER as brands were being added to it. They came back when the
                // run ended, which made it look like data loss rather than a filter.
                //
                // INTERESTING / DISMISSED / INVALIDATED are verdicts somebody reached; they are
                // fully processed rows and belong on screen throughout. Only genuinely
                // half-processed rows (PENDING, RUNNING, and the failures this gate has always
                // held back until the outcome is complete) stay hidden while the pass runs.
                clientRowFilter={campaignLeadsRowFilter}
                emptyStateContent={isOpenCampaignBuilding ? reviewingEmptyState : undefined}
                // The shortlist re-ranks itself as new scores land instead of waiting for the
                // next query, and says so while the re-score is in flight. This is the whole
                // point of the editor: change what matters, watch the ranking answer.
                liveResort
                rescoreActive={isRescoringShortlist || isBrandRerun}
                assetLedgerTitle={lang === 'fr' ? 'Leads du sourcing' : 'Sourcing leads'}
                assetLedgerBreadcrumb={lang === 'fr' ? 'Sourcing / Prospects' : 'Sourcing / Leads'}
                inlineExpand
                onExpandedIdChange={setFocusedFit}
                expandedId={focusedFitId}
                liveRefresh={isOpenCampaignActive}
                controlledSelectionIds={sourceSelectedFitIds}
                onControlledSelectionChange={handleControlledSelectionChange}
                renderCustomToolbarHeader={assetReviewToolbarHeader}
                toolbarContextMenuActions={assetReviewCampaignId ? assetReviewMode.contextMenuActions : undefined}
              />
              </>
            ) : viewMode === 'grid' ? (
              <PortfolioFitsGrid
                key={campaignFilterId}
                initialFilters={FIT_SCORE_SORT}
                lockedFilters={fitsLockedFilters}
                assetLedgerTitle={lang === 'fr' ? 'Correspondances portefeuille du sourcing' : 'Sourcing portfolio fits'}
                assetLedgerBreadcrumb={lang === 'fr' ? 'Sourcing / Correspondances portefeuille' : 'Sourcing / Portfolio fits'}
                viewModeVisual={viewMode}
                onViewModeVisualChange={setViewMode}
              />
            ) : (
              <PortfolioFitsTable
                key={campaignFilterId}
                scopedIds={portfolioReviewActive ? portfolioReviewMode.reviewIds : undefined}
                initialFilters={portfolioReviewActive ? REVIEW_MODE_INITIAL_FILTERS : FIT_SCORE_SORT}
                lockedFilters={fitsLockedFilters}
                assetLedgerTitle={lang === 'fr' ? 'Correspondances portefeuille du sourcing' : 'Sourcing portfolio fits'}
                assetLedgerBreadcrumb={lang === 'fr' ? 'Sourcing / Correspondances portefeuille' : 'Sourcing / Portfolio fits'}
                viewModeVisual={viewMode}
                onViewModeVisualChange={setViewMode}
                inlineExpand
                onExpandedIdChange={setFocusedFit}
                expandedId={focusedFitId}
                liveRefresh={isOpenCampaignActive}
                controlledSelectionIds={sourceSelectedFitIds}
                onControlledSelectionChange={handleControlledSelectionChange}
                renderCustomToolbarHeader={portfolioReviewToolbarHeader}
                toolbarContextMenuActions={portfolioReviewCampaignId ? portfolioReviewMode.contextMenuActions : undefined}
              />
            )}
          </div>
        </div>
      );
    }

    if (targetFocus.entityType === 'Property') {
      if (!resolvedPropertyIdForData) {
        return (
          <div className="rounded-xl border border-[#2A2A2A] bg-[#141414] px-4 py-6 text-sm text-white/55">
            {lang === 'fr'
              ? "Sélectionnez d'abord un actif pour afficher cette liste."
              : 'Select an asset target first to display this list.'}
          </div>
        );
      }
      // NO CAMPAIGN OPEN => NO LIST. Source has exactly two pages: the campaign dashboard, or
      // ONE campaign's shortlist. There is no third "every lead this asset ever produced" view.
      //
      // That view existed, and it was reachable by accident: `sourceSelectedCampaignId` lives in
      // memory, not in the URL, so REFRESHING the browser inside a running campaign dropped it and
      // this branch fell back to a property-wide list. The result mixed every campaign the asset
      // has ever run, different dates, different rubrics, so different point scales side by side
      // (7/10 next to 18/20 next to 38/40), presented as one ranking. It is not one ranking, and
      // no user asked for it.
      //
      // Falling through to the dashboard is the honest answer to "which shortlist?" when we have
      // lost track of which one was open.
      //
      // BUT NOT WHILE A CAMPAIGN IS BEING DRAFTED. A draft has no campaign id by definition, so
      // this test alone sent every "New campaign" click on an ASSET straight back to the dashboard,
      // beside a right bar showing the setup form. Reported twice. The portfolio branch above asks
      // the same question; both now ask it the same way.
      if (!selectedCampaignId && !isDraftCampaign) {
        return (
          <div className="flex flex-col pb-10">
            {/* No Database folder row. Assets / Tenants / Contacts opened raw inventory tables
                straight off the dashboard, which is a door OUT of the work: the dashboard is
                campaigns, and a shortlist is what the user came for. The tables themselves are
                untouched and still reachable from the Tables tool. */}
            <CampaignDashboard portfolioId={resolvedPortfolioId} onSelect={setSelectedCampaignId} />
          </div>
        );
      }

      // A campaign IS open: scope the leads to THAT campaign, keyed by it so a launch remounts
      // onto the new run, with live refresh while it is still scoring.
      // The filters and the reveal gate are the hoisted `assetLeadsLockedFilters` /
      // `assetLeadsRowFilter` above - deliberately NOT recomputed here. A second local copy is how
      // the asset branch drifted from the portfolio one before, and noBulkLeadList.test.ts pins
      // that the asset lock appears in exactly one object.
      //
      // The key carries the draft sentinel for the same reason the filters do: a draft has no
      // campaign id, and keying on null would let the table keep the previous campaign's rows
      // mounted underneath the new campaign's empty shell.
      const assetLeadsKey = campaignFilterId;
      if (viewMode === 'grid') {
        return (
          <LeadsGrid
            key={assetLeadsKey}
            initialFilters={LEAD_SCORE_SORT}
            lockedFilters={assetLeadsLockedFilters}
            assetLedgerTitle={lang === 'fr' ? 'Prospects liés à la sélection' : 'Leads linked to selection'}
            assetLedgerBreadcrumb={lang === 'fr' ? 'Sourcing / Leads' : 'Sourcing / Leads'}
            viewModeVisual={viewMode}
            onViewModeVisualChange={setViewMode}
          />
        );
      }
      return (
        <LeadsTable
          key={assetLeadsKey}
          glass
          toolbarLead={<>{dismissedToggle}{merchPlanButton}{ownerChip}</>}
          initialFilters={LEAD_SCORE_SORT}
          lockedFilters={assetLeadsLockedFilters}
          clientRowFilter={selectedCampaignId ? assetLeadsRowFilter : undefined}
          emptyStateContent={isOpenCampaignBuilding && selectedCampaignId ? reviewingEmptyState : undefined}
          liveResort
          rescoreActive={isRescoringShortlist || isBrandRerun}
          // The asset column is hidden when the list is about ONE asset, and shown when the run
          // covers several: without it the same brand appears once per asset, three rows, three
          // different scores, no way to tell which centre each belongs to.
          propertyLocked={!openCampaignCoversManyAssets}
          leadLockedUnitId={resolvedUnitIdForData || undefined}
          assetLedgerTitle={lang === 'fr' ? 'Prospects liés à la sélection' : 'Leads linked to selection'}
          assetLedgerBreadcrumb={lang === 'fr' ? 'Sourcing / Leads' : 'Sourcing / Leads'}
          viewModeVisual={viewMode}
          onViewModeVisualChange={setViewMode}
          // Clicking a brand opens the detail INLINE, under its row. Without this the table falls
          // back to focusEntityUI and the whole canvas is replaced by the Lead page, which throws
          // the user out of the run they are watching.
          inlineExpand
          onExpandedIdChange={setFocusedFit}
          expandedId={focusedFitId}
          liveRefresh={isOpenCampaignActive}
          controlledSelectionIds={sourceSelectedFitIds}
          onControlledSelectionChange={handleControlledSelectionChange}
        />
      );
    }

    // A UNIT SUBJECT, after unit sourcing was removed. There is no unit engine any more, so the
    // honest answer to "what could go in this unit" is its asset's shortlist narrowed to the unit:
    // the same leads, the same inline expand, no separate family behind it.
    return (
      <LeadsTable
        key={unitCampaignScoped ? campaignFilterId : 'unit'}
        glass
        toolbarLead={ownerChip}
        initialFilters={LEAD_SCORE_SORT}
        lockedFilters={unitLeadsLockedFilters}
        leadLockedUnitId={resolvedUnitIdForData || undefined}
        assetLedgerTitle={lang === 'fr' ? 'Prospects liés à la sélection' : 'Leads linked to selection'}
        assetLedgerBreadcrumb={lang === 'fr' ? 'Sourcing / Leads' : 'Sourcing / Leads'}
        inlineExpand
        onExpandedIdChange={setFocusedFit}
        expandedId={focusedFitId}
        liveRefresh={isOpenCampaignActive}
        viewModeVisual={viewMode}
        onViewModeVisualChange={setViewMode}
      />
    );
  };

  return (
    <div
      className={isShowingDashboard ? 'relative h-full w-full bg-black px-2 pb-6 pt-5' : 'relative h-full w-full bg-black px-6 pb-6 pt-[68px]'}
      data-source-type
      data-campaign-active={isOpenCampaignActive ? '1' : '0'}
      data-campaign-status={String(selectedCampaignForScope?.status ?? '')}
    >
      <ResearchRowStyles campaignId={selectedCampaignId} />
      {/* Agent-working aura, the same pulsing glow the Analyst shows while its agent works,
          lit while the sourcing agent is actively running a campaign. */}
      <div aria-hidden className={`agent-working-aura${showWorkingAura ? ' on' : ''}`} />
      {/* Enrichment progress, a seamless slide through the dimensions of Egyptian blue. */}
      <EnrichProgressBar />
      <div className="flex h-full w-full flex-col">
        {/* THE PAGE NAMES ITSELF, like a Notion page: the title belongs to the thing you are
            reading, not to the chrome above it. It used to live centred in the top bandeau, where
            it was small, far from the table it labelled, and competing with the navigation.
            Given room here it reads as a heading, and the bandeau is left to navigation alone.
            The string is the same one the bandeau used, so there is still exactly one definition
            of what this page is called. */}
        {/* pl-4 puts the page glyph on the SAME left edge as the table below it. The table
            carries its own inner padding, so a header flush against the container sat 16px
            to the left of the toolbar and the row checkboxes - three staggered left edges
            down one page. Everything now starts on one line. */}
        <div className={isShowingDashboard ? 'shrink-0 pb-5 pl-4 pr-4 pt-1' : 'shrink-0 pb-5 pl-4 pt-1'}>
          <div className="flex items-center gap-2.5">
            {/* THE SHORTLIST GETS A GLYPH, THE DASHBOARD DOES NOT. The dashboard is a list of
                pages, not a page about something, so an icon there labels nothing - and the cards
                below carry their own identity already.

                Dark glass by default; double-click to choose an emoji instead. The drawn mark is
                the fallback because a typed emoji renders as an empty box wherever no colour
                emoji font is installed, so it cannot be the only thing a page has. */}
            {isShowingDashboard ? null : openCampaignLogoUrl ? (
              // The asset's own logo stands for the campaign (owner, 2026-09-26); the glyph is for
              // runs whose asset has none.
              <BrandMark logoUrl={openCampaignLogoUrl} name={typeof openCampaignRow?.targetName === 'string' ? openCampaignRow.targetName : ''} size={36} radius={10} />
            ) : (
            <span className="relative">
              <PageEmoji
                emoji={pageEmoji || undefined}
                fallback="🎯"
                size={30}
                onDoubleClick={() => setEmojiPickerOpen((open) => !open)}
                title={lang === 'fr' ? 'Double-cliquez pour choisir un emoji' : 'Double-click to choose an emoji'}
              />
              {emojiPickerOpen ? (
                <>
                  {/* Click-away, as a sibling rather than a document listener: it cannot leak past
                      unmount and it cannot swallow the first click on anything else. */}
                  <span className="fixed inset-0 z-40" onClick={() => setEmojiPickerOpen(false)} />
                  <span className="absolute left-0 top-full z-50 mt-2 block">
                    <SourceEmojiPicker onPick={pickPageEmoji} hasEmoji={Boolean(pageEmoji)} lang={lang} />
                  </span>
                </>
              ) : null}
            </span>
            )}
            {/* DOUBLE-CLICK TO RENAME, like the page title it is pretending to be. Editing writes
                the campaign's own name (config.name), which is what the card and the header
                already read when it is set - so a rename shows up in both without a second store.
                The dashboard's title is not a thing that can be renamed, so it stays plain text. */}
            {isRenamingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(event) => setTitleDraft(event.target.value)}
                // ONE COMMIT PATH: blur. Enter used to save directly, which unmounted the input
                // and then fired blur - saving a second time, a second PATCH for the same name.
                // Enter now just leaves the field and lets blur do the work.
                onBlur={() => { if (renameCancelledRef.current) { renameCancelledRef.current = false; setIsRenamingTitle(false); return; } void commitTitle(); }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
                  // Escape abandons the edit: a rename you did not mean is worse than no rename,
                  // so the draft is thrown away rather than saved on the way out.
                  if (event.key === 'Escape') { event.preventDefault(); renameCancelledRef.current = true; event.currentTarget.blur(); }
                }}
                className="min-w-0 flex-1 rounded-[12px] border border-[#2A2A2A] bg-transparent px-2 font-medium text-white outline-none focus:border-[#444444]"
                style={{ fontSize: 26, lineHeight: '32px' }}
              />
            ) : (
              <h1
                className="min-w-0 truncate font-medium text-white"
                style={{ fontSize: 26, lineHeight: '32px' }}
                title={
                  isShowingDashboard
                    ? sourceHeaderTitle
                    : (lang === 'fr' ? 'Double-cliquez pour renommer' : 'Double-click to rename')
                }
                onDoubleClick={isShowingDashboard ? undefined : () => {
                  setTitleDraft(sourceHeaderTitle);
                  setIsRenamingTitle(true);
                }}
              >
                {sourceHeaderTitle}
              </h1>
            )}
            {isShowingDashboard ? <div className="ml-auto"><SourceDashboardToggle /></div> : null}
          </div>
          {/* Properties of the page, Notion-style. Only the owner for now, and only once we know
              who it is: a row reading "Owner" with nothing after it teaches the reader nothing and costs a line. */}
          {/* An asset shortlist carries the owner on its table toolbar instead (owner, 2026-09-28):
              one line for who and what can be done, not two. */}
          {ownerChip && scope !== 'asset' ? (
            <div className="mt-3">{ownerChip}</div>
          ) : null}
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Editor: campaign dashboard, or a campaign's brand fits */}
          <div className="min-h-0 flex-1 overflow-auto">
            {renderSourcingData()}
          </div>
        </div>
      </div>
    </div>
  );
};
